
import React, { useState } from 'react';
import Button from './ui/Button';
import Card from './ui/Card';

interface SupportTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: {
    id: number;
    name: string;
    email: string;
  };
}

const SupportTicketModal = ({ isOpen, onClose, client }: SupportTicketModalProps) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ticketResult, setTicketResult] = useState<{ success: boolean; caseNumber?: string; error?: string } | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTicketResult(null);

    try {
      const response = await fetch('/api/client/support-tickets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          client_id: client.id,
          client_name: client.name,
          client_email: client.email,
          subject,
          message,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setTicketResult({ success: true, caseNumber: data.case_number });
      } else {
        setTicketResult({ success: false, error: data.error || 'Error al enviar el ticket' });
      }
    } catch (err) {
      setTicketResult({ success: false, error: 'Error de conexión con el servidor' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setSubject('');
    setMessage('');
    setTicketResult(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
      <Card className="w-full max-w-lg relative bg-slate-800 border-slate-700 shadow-2xl p-0 overflow-hidden">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors z-10">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="p-8">
          {ticketResult?.success ? (
            <div className="text-center py-8 animate-fade-in">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Ticket Abierto con Éxito</h2>
              <p className="text-slate-400 mb-6 font-mono text-lg">
                Número de caso: <span className="text-blue-400 font-bold">{ticketResult.caseNumber}</span>
              </p>
              <div className="bg-slate-900/50 rounded-xl p-4 mb-8 text-sm text-slate-300 border border-slate-700">
                Se ha enviado una copia de este ticket a su correo <span className="text-blue-300">{client.email}</span>. Nuestro equipo técnico le responderá lo antes posible.
              </div>
              <Button onClick={handleReset} variant="primary" className="w-full py-3">
                Entendido
              </Button>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-white mb-2">Contactar Soporte</h2>
              <p className="text-slate-400 mb-6">
                Explique su problema detalladamente y nuestro equipo le asignará un técnico especializado.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Asunto</label>
                  <input
                    type="text"
                    required
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Ej: Problema con el acceso al Proyecto X"
                    className="w-full bg-slate-900 text-white p-3 rounded-lg border border-slate-700 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Descripción del problema</label>
                  <textarea
                    required
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={5}
                    placeholder="Escriba aquí los detalles..."
                    className="w-full bg-slate-900 text-white p-3 rounded-lg border border-slate-700 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all resize-none"
                  ></textarea>
                </div>

                {ticketResult?.error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-lg">
                    {ticketResult.error}
                  </div>
                )}

                <div className="pt-4 flex gap-4">
                  <Button type="button" variant="ghost" onClick={onClose} className="flex-1">
                    Cancelar
                  </Button>
                  <Button type="submit" variant="primary" className="flex-1" disabled={isSubmitting}>
                    {isSubmitting ? 'Enviando...' : 'Abrir Ticket'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      </Card>
    </div>
  );
};

export default SupportTicketModal;
